import {HTMLAttributes, useLayoutEffect, useRef} from 'react';

import {useLocation} from 'react-router-dom';
import styled, {css} from 'styled-components';

import {TextThemes} from 'components/design-guide';
import {generateTheme} from 'components/design-guide/utils';
import {Breakpoints, Spacing, FontSize, FontFamily, FontWeight} from 'styles/Base';
import {Colors} from 'styles/Colors';
import {MarginProps, PaddingProps, generateMargins, generatePadding} from 'utils/components';

type HeaderProps = {textTheme?: TextThemes};

const headingFontFamily = css`
  font-family: ${FontFamily.AVENIR};
`;
const headingFontFamilyRebrand = css`
  font-family: ${FontFamily.RHYMES_MEDIUM};
`;
const largeHeadingFontColor = css<HeaderProps>`
  color: ${({textTheme}: HeaderProps) => generateTheme(textTheme)};
`;
const smallHeadingFontColor = css`
  color: ${Colors.BROWN_GREY};
`;

const H1Styles = css`
  ${headingFontFamilyRebrand}
  ${largeHeadingFontColor}
  font-size: ${FontSize.TWO_625};
  line-height: ${Spacing.THREE_125};
  font-weight: ${FontWeight.MEDIUM};

  ${`@media (${Breakpoints.MOBILE})`} {
    font-size: ${FontSize.TWO};
    line-height: ${Spacing.TWO_375};
  }
`;

const H2Styles = css`
  ${headingFontFamilyRebrand}
  ${largeHeadingFontColor}
  font-size: ${FontSize.TWO};
  line-height: ${Spacing.TWO_375};
  font-weight: ${FontWeight.MEDIUM};
  margin: ${Spacing.ZERO};

  ${`@media (${Breakpoints.MOBILE})`} {
    font-size: ${FontSize.ONE_5};
    line-height: ${Spacing.ONE_875};
  }
`;

const H3Styles = css`
  ${headingFontFamily}
  ${largeHeadingFontColor}
  font-size: ${FontSize.ONE_5};
  line-height: ${Spacing.ONE_875};
  font-weight: ${FontWeight.MEDIUM};
  margin: ${Spacing.ZERO};

  ${`@media (${Breakpoints.MOBILE})`} {
    font-size: ${FontSize.ONE_125};
    line-height: ${Spacing.ONE_4375};
  }
`;

const H4Styles = css`
  ${headingFontFamily}
  ${largeHeadingFontColor}
  font-size: ${FontSize.ONE_125};
  line-height: ${Spacing.ONE_4375};
  font-weight: ${FontWeight.MEDIUM};
  margin: ${Spacing.ZERO};

  ${`@media (${Breakpoints.MOBILE})`} {
    font-size: ${FontSize.ONE};
    line-height: ${Spacing.ONE_25};
  }
`;
const H5Styles = css`
  ${headingFontFamily}
  ${smallHeadingFontColor}
  font-size: ${FontSize.ONE};
  line-height: ${Spacing.ONE_25};
  font-weight: ${FontWeight.MEDIUM};
  margin: ${Spacing.ZERO};

  ${`@media (${Breakpoints.MOBILE})`} {
    font-size: ${FontSize.ZERO_875};
    line-height: ${Spacing.ONE_125};
  }
`;

type HProps = HTMLAttributes<HTMLHeadElement> & MarginProps & PaddingProps & HeaderProps;

const StyledH1 = styled.h1<HProps>`
  ${H1Styles}
  ${(props: HProps) => generateMargins(props)}
  ${(props: HProps) => generatePadding(props)}
  &:focus {
    outline: none;
  }
`;

export const H1 = (props: Parameters<typeof StyledH1>[0] & {focusOnMount?: boolean}) => {
  const selfRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  useLayoutEffect(() => {
    props.focusOnMount !== false && selfRef.current?.focus();
  }, [props.focusOnMount, location.pathname]);

  return <StyledH1 {...props} tabIndex={-1} ref={selfRef} />;
};
export const H2 = styled.h2<HProps>`
  ${H2Styles}
  ${(props: HProps) => generateMargins(props)}
  ${(props: HProps) => generatePadding(props)}
`;
export const H3 = styled.h3<HProps>`
  ${H3Styles}
  ${(props: HProps) => generateMargins(props)}
  ${(props: HProps) => generatePadding(props)}
`;
export const H4 = styled.h4<HProps>`
  ${H4Styles}
  ${(props: HProps) => generateMargins(props)}
  ${(props: HProps) => generatePadding(props)}
`;
export const H5 = styled.h5<HProps>`
  ${H5Styles}
  ${(props: HProps) => generateMargins(props)}
  ${(props: HProps) => generatePadding(props)}
`;

export const VariableHeader = (props: HProps & {headerType: 'h1' | 'h2' | 'h3' | 'h4' | 'h5'}) => {
  const {headerType} = props;
  const HComponent =
    headerType === 'h1' ? H1 : headerType === 'h2' ? H2 : headerType === 'h3' ? H3 : headerType === 'h4' ? H4 : H5;
  return <HComponent {...props} />;
};

//This is a unique custom header due to a result of accessability findings.
export const WizardHeaderText = styled.p<HProps>`
  ${H3Styles}
`;

//This is a unique custom header due to style of billing action cards.
export const QuickActionsLargeText = styled.p<HProps>`
  ${(props: HProps) => generateMargins(props)}
  ${H1Styles}
`;

//This is a unique custom header due to tyle of billing cards.
export const CardSummaryLargeText = styled.p<HProps>`
  ${(props: HProps) => generateMargins(props)}
  ${H2Styles}
`;

//This is a unique custom header due to style of billing cards.
export const CardSummaryMediumText = styled.p<HProps>`
  ${(props: HProps) => generateMargins(props)}
  ${H4Styles}
`;
